import { Component, computed, inject, Signal } from '@angular/core';
import { DetailBaseComponent } from '@kinet-open-network/client/core/detail-base';
import {
  confirmDialog,
  HeaderButton,
  IndicatorComponent,
  SidebarLayoutComponent,
  SidebarLayoutHeaderDirective,
  SidebarTabsConfig,
  toast,
} from '@kinet-open-network/client/shared/ui';
import {
  clearDirtyState,
  DetailAction,
} from '@kinet-open-network/client/shared/util';
import {
  ChangePriceFlowStatus,
  changePricesStatusOptions,
} from '@kinet-open-network/shared/data-access/model-pbgas';
import { MenuItem } from 'primeng/api';
import { FlowChangePrices } from '../data-access/flow-change-prices.model';
import { FlowChangePricesService } from '../data-access/flow-change-prices.service';
import { FlowChangePricesStore } from '../data-access/flow-change-prices.store';

@Component({
  selector: 'kon-flow-change-prices-detail',
  imports: [
    SidebarLayoutComponent,
    SidebarLayoutHeaderDirective,
    IndicatorComponent,
  ],
  template: `
    <kon-sidebar-layout
      [actions]="actions()"
      [style]="{ width: '90rem', 'max-width': '99%' }"
      [buttons]="headerButtons()"
      [tabs]="tabsConfig"
    >
      @if (!isCreate()) {
        <div *konSidebarHeader>
          <kon-indicator
            [value]="store.model()?.status"
            [options]="changePriceStatusOptions"
          ></kon-indicator>
        </div>
      }
    </kon-sidebar-layout>
  `,
})
export class FlowChangePricesDetailComponent extends DetailBaseComponent<FlowChangePrices> {
  store = inject(FlowChangePricesStore);
  service = inject(FlowChangePricesService);
  loading = this.store.loading;
  model = this.store.model;

  tabsConfig: SidebarTabsConfig = {
    tabs: [
      {
        value: 'default',
        label: 'Všeobecné',
        routerLink: ['.'],
      },
      {
        value: 'documents',
        label: 'Dokumenty',
        routerLink: ['documents'],
        visible: () => this.showDocumentsTab(),
      },
      {
        value: 'audits',
        label: 'Audit',
        routerLink: ['audits'],
        visible: () => !this.isCreate(),
      },
    ],
  };

  changePriceStatusOptions = changePricesStatusOptions;

  showDocumentsTab = computed(
    () => !this.isCreate() && this.ability().can('read', 'Document'),
  );

  actions = computed<MenuItem[]>(() => [
    {
      label: 'Zrušiť',
      icon: 'pi pi-times',
      visible:
        this.isEdit() && this.model()?.status === ChangePriceFlowStatus.ready,
      command: () =>
        confirmDialog({
          header: 'Zrušiť Cenovú ponuku',
          message: 'Naozaj chcete zrušiť cenovú ponuku?',
          icon: 'pi pi-times',
          accept: async () =>
            this.store.processStep(this.model()!.id, {
              status: ChangePriceFlowStatus.rejected,
            }),
        }),
    },
  ]);

  headerButtons: Signal<HeaderButton[]> = computed(() => [
    {
      label: 'Akceptovať a aplikovať ceny',
      icon: 'pi pi-check-circle',
      severity: 'success',
      outlined: true,
      visible:
        this.isEdit() &&
        [ChangePriceFlowStatus.ready, ChangePriceFlowStatus.rejected].includes(
          this.model()?.status ?? ChangePriceFlowStatus.ready,
        ),
      action: async () =>
        confirmDialog({
          header: 'Akceptovať a aplikovať ceny',
          message: 'Naozaj chcete akceptovať cenovú ponuku a aplikovať ceny?',
          icon: 'pi pi-check-circle',
          accept: () =>
            this.store.processStep(this.model()!.id, {
              status: ChangePriceFlowStatus.accepted,
            }),
        }),
    },
    {
      label: 'Zamietnuť',
      icon: 'pi pi-times',
      severity: 'danger',
      outlined: true,
      visible:
        this.isEdit() && this.model()?.status === ChangePriceFlowStatus.ready,
      action: async () =>
        confirmDialog({
          header: 'Zamietnuť cenovú ponuku',
          message: 'Naozaj chcete zamietnuť cenovú ponuku?',
          icon: 'pi pi-times',
          accept: () =>
            this.store.processStep(this.model()!.id, {
              status: ChangePriceFlowStatus.rejected,
            }),
        }),
    },
  ]);

  override async saveModel(
    action: DetailAction,
    data?: FlowChangePrices | null,
  ) {
    const id = this.store.modelId();
    const modelData = data || this.store.model();

    if (!modelData) return;

    if (action === 'edit' && id) {
      return this.store.processStep(id, modelData).then(() => {
        this.refreshList();
        clearDirtyState(this.route);
        if (this.navigateToListAfterSave) {
          this.navigateToList();
        }
        toast({ severity: 'success', detail: 'Cenová ponuka uložená' });
      });
    }

    if (action === 'create') {
      return this.store.create(modelData).then(() => {
        this.refreshList();
        clearDirtyState(this.route);
        if (this.navigateToListAfterSave) {
          this.navigateToList();
        }
        toast({ severity: 'success', detail: 'Cenová ponuka vytvorená' });
      });
    }
  }

  override navigateToList() {
    console.log('navigateToList called', this.action);
    this.router.navigate(['.'], {
      relativeTo: this.isEdit() ? this.route.parent?.parent : this.route.parent,
    });
  }
}
